import { metaFor } from '../data/categories';
import { productSummary } from '../data/products';
import { productCalloutHtml } from './callouts';
import { relatedInvestigations } from '../data/investigations';
import { withPlayerName } from '../player-name';
import { trendRatio } from '../trending';
import type { Entry } from '../types';
import { countryName, escapeHtml, formatCount, formatDate } from './util';

const TYPE_LABEL: Record<Entry['type'], string> = {
  tool: 'Tool',
  web: 'Site',
  app: 'iOS App',
  game: 'Game',
  product: 'Product',
};

/** Last 30 calendar days of daily visits ending at the newest recorded day, zero-filled. */
export function last30Series(daily: Record<string, { pv: number; v: number }>): { date: string; v: number }[] {
  const keys = Object.keys(daily).sort();
  if (keys.length === 0) return [];
  const end = new Date(`${keys[keys.length - 1]}T00:00:00Z`);
  const out: { date: string; v: number }[] = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date(end.getTime() - i * 86_400_000);
    const iso = d.toISOString().slice(0, 10);
    out.push({ date: iso, v: daily[iso]?.v ?? 0 });
  }
  return out;
}

function barsHtml(entry: Entry): string {
  const daily = entry.stats?.daily;
  if (!daily) return '';
  const series = last30Series(daily);
  if (series.length === 0) return '';
  const total = series.reduce((n, s) => n + s.v, 0);
  const max = Math.max(...series.map((s) => s.v), 1);
  const bw = 100 / series.length;
  // Empty days keep a faint 2px stub so the row reads as a baseline of ticks
  // rather than a single stray bar when traffic is sparse.
  const bars = series
    .map((s, i) => {
      const h = s.v > 0 ? Math.max((s.v / max) * 30, 4) : 2;
      const cls = s.v > 0 ? 'xp-bar' : 'xp-bar xp-bar--empty';
      const x = (i * bw + bw * 0.15).toFixed(2);
      const w = (bw * 0.7).toFixed(2);
      return `<rect class="${cls}" x="${x}" y="${(34 - h).toFixed(2)}" width="${w}" height="${h.toFixed(2)}"><title>${s.date}: ${s.v} visits</title></rect>`;
    })
    .join('');
  return `
    <div class="xp-chart">
      <div class="xp-chart-head">
        <span class="xp-label">Daily visits</span>
        <span class="xp-chart-note">${total} in 30 days</span>
      </div>
      <svg viewBox="0 0 100 34" preserveAspectRatio="none" role="img" aria-label="Daily visits, last 30 days">${bars}</svg>
    </div>`;
}

function figuresHtml(entry: Entry): string {
  if (entry.product) return ''; // a product's analytics are never shown
  const agg = entry.stats?.agg;
  if (!agg) return '';
  const trend = trendRatio(agg.v7, agg.v7p);
  const up = trend >= 1;
  return `
    <div class="stats">
      <div class="figures">
        <div class="figure"><span class="figure-num">${formatCount(agg.v7)}</span><span class="xp-label">Visits · 7d</span></div>
        <div class="figure"><span class="figure-num">${formatCount(agg.v30)}</span><span class="xp-label">Visits · 30d</span></div>
        <div class="figure"><span class="figure-num">${formatCount(agg.vAll)}</span><span class="xp-label" title="Since ${escapeHtml(formatDate(agg.since))}">All time</span></div>
        <div class="figure"><span class="figure-num figure-${up ? 'up' : 'down'}">${up ? '▲' : '▼'} ${trend.toFixed(2)}×</span><span class="xp-label" title="Unique visits this week vs. last">Week / week</span></div>
      </div>
      ${barsHtml(entry)}
    </div>`;
}

/**
 * "Related reading" — In the Detail investigations that read from this entry's
 * dataset, joined by the entry's slug against each piece's hubExplorers. Renders
 * nothing when there's no match, and lists every match (scales to several
 * investigations per data explorer). Links open the newsroom in a new tab.
 */
function relatedReadingHtml(entry: Entry): string {
  const related = relatedInvestigations(entry.slug);
  if (related.length === 0) return '';
  const items = related
    .map(
      (inv) =>
        `<li class="xp-related-item">
          <a class="xp-related-link" href="${escapeHtml(inv.url)}" target="_blank" rel="noopener">${escapeHtml(inv.title)}</a>
          ${inv.dek ? `<span class="xp-related-dek">${escapeHtml(inv.dek)}</span>` : ''}
        </li>`,
    )
    .join('');
  return `
    <div class="xp-related">
      <p class="xp-related-lab">Related reading · In the Detail</p>
      <ul class="xp-related-list">${items}</ul>
    </div>`;
}

/** The inline expansion panel rendered directly below an entry's row. A product's
 *  opens with a callout saying it isn't part of the experiment, except in the
 *  Products view, which says so once at the top. */
export function expansionHtml(entry: Entry, opts: { productsView?: boolean } = {}): string {
  const meta = metaFor(entry.category);
  const tags = entry.tags.map(escapeHtml).join(' · ');
  const place = entry.country ? ` · ${escapeHtml(countryName(entry.country))}` : '';
  const figures = figuresHtml(entry);
  const summary = productSummary(entry);
  const kind = `${TYPE_LABEL[entry.type]}${summary ? ` · ${escapeHtml(summary)}` : ''}`;
  const shipped = entry.product?.comingSoon ? 'coming soon' : `shipped ${escapeHtml(formatDate(entry.date))}`;
  const launch = entry.product?.linkLabel ?? 'Launch';
  return `
    <div class="row-expansion${figures ? '' : ' row-expansion--solo'}" data-expansion="${escapeHtml(entry.slug)}">
      <div class="xp-main">
        ${entry.product && !opts.productsView ? productCalloutHtml() : ''}
        <p class="xp-desc">${escapeHtml(entry.description)}</p>
        ${tags ? `<p class="xp-tags">${tags}</p>` : ''}
        <p class="xp-meta">${kind} · ${escapeHtml(meta.label)}${place} · ${shipped}</p>
        <p class="xp-actions">
          ${entry.url ? `<a class="xp-launch" href="${escapeHtml(withPlayerName(entry.url, entry.type))}" target="_blank" rel="noopener">${escapeHtml(launch)} ↗</a>` : ''}
          ${entry.repo ? `<a class="xp-source" href="${escapeHtml(entry.repo)}" target="_blank" rel="noopener">Source ↗</a>` : ''}
        </p>
        ${relatedReadingHtml(entry)}
      </div>
      ${figures ? `<div class="xp-side">${figures}</div>` : ''}
    </div>`;
}
